import { Link } from "react-router-dom";
import {
  STUDIO_DEMO_LAUNCHER_PRESETS,
  buildStudioDemoPreviewHref,
  type StudioDemoLauncherId,
} from "../../lib/studio/studioDemoLauncher";
import type { VetCanStudioStepId } from "../../lib/studio/studioReadModel";

function tone(active: boolean) {
  if (active) {
    return {
      border: "1px solid rgba(41, 84, 103, 0.24)",
      background:
        "linear-gradient(180deg, rgba(240, 248, 250, 0.96), rgba(255, 252, 248, 0.98))",
      pillBackground: "rgba(41, 84, 103, 0.1)",
      pillColor: "#295467",
      shadow: "0 22px 42px rgba(41, 84, 103, 0.12)",
    };
  }

  return {
    border: "1px solid rgba(54, 44, 35, 0.1)",
    background:
      "linear-gradient(180deg, rgba(255, 252, 248, 0.96), rgba(251, 248, 243, 0.98))",
    pillBackground: "rgba(54, 44, 35, 0.08)",
    pillColor: "#5e544b",
    shadow: "0 18px 34px rgba(77, 61, 44, 0.08)",
  };
}

export default function StudioDemoLauncher(props: {
  activeDemoId: StudioDemoLauncherId;
  onSelectDemo: (demoId: StudioDemoLauncherId) => void;
  onOpenStudioStep: (stepId: VetCanStudioStepId, demoId: StudioDemoLauncherId) => void;
}) {
  return (
    <section
      aria-labelledby="studio-demo-launcher-heading"
      style={{
        marginTop: "1.2rem",
        borderRadius: 30,
        border: "1px solid rgba(54, 44, 35, 0.1)",
        background:
          "linear-gradient(180deg, rgba(255, 252, 248, 0.98), rgba(245, 241, 234, 0.98))",
        padding: "1.25rem",
        boxShadow: "0 26px 56px rgba(77, 61, 44, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ maxWidth: 780 }}>
          <div
            style={{
              fontSize: "0.78rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "#7b726a",
              fontWeight: 700,
            }}
          >
            Walkthrough presets
          </div>
          <h2
            id="studio-demo-launcher-heading"
            style={{
              margin: "0.45rem 0 0",
              fontSize: "clamp(1.55rem, 2.7vw, 2.15rem)",
              lineHeight: 1.06,
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: "#231f1a",
            }}
          >
            Preset walkthroughs for Studio and Preview
          </h2>
          <p style={{ margin: "0.72rem 0 0", color: "#4d453d", lineHeight: 1.7 }}>
            Start from the clinic walkthrough first, then use the other bounded
            presets only when install-week review needs a different showcase frame.
          </p>
        </div>

        <div style={{ display: "flex", gap: "0.55rem", flexWrap: "wrap" }}>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "0.3rem 0.64rem",
              background: "rgba(44, 93, 67, 0.12)",
              color: "#214735",
              fontSize: "0.74rem",
              fontWeight: 700,
            }}
          >
            internal-only
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              borderRadius: 999,
              padding: "0.3rem 0.64rem",
              background: "rgba(162, 62, 62, 0.12)",
              color: "#812d2d",
              fontSize: "0.74rem",
              fontWeight: 700,
            }}
          >
            no live routing
          </span>
        </div>
      </div>

      <p style={{ margin: "0.9rem 0 0", color: "#5b534b", lineHeight: 1.65 }}>
        Open the correct Studio step or Preview scenario with the preset context
        already selected.
      </p>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "0.95rem",
        }}
      >
        {STUDIO_DEMO_LAUNCHER_PRESETS.map((preset) => {
          const active = preset.id === props.activeDemoId;
          const styles = tone(active);

          return (
            <section
              key={preset.id}
              aria-label={preset.label}
              style={{
                borderRadius: 26,
                border: styles.border,
                background: styles.background,
                boxShadow: styles.shadow,
                padding: "1rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.42rem",
                    borderRadius: 999,
                    padding: "0.28rem 0.6rem",
                    background: styles.pillBackground,
                    color: styles.pillColor,
                    fontSize: "0.73rem",
                    fontWeight: 700,
                  }}
                >
                  {active ? "Selected" : "Showcase"}
                </span>
                <span
                  style={{
                    color: "#7b726a",
                    fontSize: "0.76rem",
                    fontWeight: 700,
                  }}
                >
                  {preset.audience}
                </span>
              </div>

              <h3
                style={{
                  margin: "0.85rem 0 0",
                  fontSize: "1.08rem",
                  lineHeight: 1.2,
                  color: "#26221d",
                }}
              >
                {preset.label}
              </h3>

              <p style={{ margin: "0.55rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
                {preset.summary}
              </p>

              <p
                style={{
                  margin: "0.45rem 0 0",
                  color: "#6b6258",
                  lineHeight: 1.6,
                  fontSize: "0.92rem",
                }}
              >
                {preset.boundaryNote}
              </p>

              <div style={{ marginTop: "0.85rem", display: "grid", gap: "0.55rem" }}>
                {preset.showcaseFacts.map((fact) => (
                  <div
                    key={`${preset.id}_${fact}`}
                    style={{
                      borderRadius: 16,
                      border: "1px solid rgba(54, 44, 35, 0.08)",
                      background: "rgba(255, 255, 255, 0.72)",
                      padding: "0.72rem 0.78rem",
                      color: "#3f3831",
                      lineHeight: 1.55,
                    }}
                  >
                    {fact}
                  </div>
                ))}
              </div>

              <div
                style={{
                  marginTop: "0.95rem",
                  display: "flex",
                  gap: "0.65rem",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() => props.onOpenStudioStep(preset.entryStep, preset.id)}
                  style={{
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    border: "1px solid rgba(41, 84, 103, 0.18)",
                    background: "linear-gradient(135deg, #173847, #285466)",
                    color: "#f7f4ee",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Open Studio at {preset.entryStepLabel}
                </button>

                <Link
                  to={buildStudioDemoPreviewHref({
                    demoId: preset.id,
                    returnStep: preset.entryStep,
                  })}
                  onClick={() => props.onSelectDemo(preset.id)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    textDecoration: "none",
                    border: "1px solid rgba(54, 44, 35, 0.12)",
                    background: "#fff9f2",
                    color: "#3e372f",
                    fontWeight: 700,
                  }}
                >
                  Open Preview scenario
                </Link>

                <button
                  type="button"
                  onClick={() => props.onOpenStudioStep("deploy_readiness", preset.id)}
                  style={{
                    borderRadius: 999,
                    padding: "0.76rem 1rem",
                    border: "1px solid rgba(54, 44, 35, 0.12)",
                    background: "rgba(255, 255, 255, 0.82)",
                    color: "#3e372f",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Open Deploy Readiness
                </button>
              </div>
            </section>
          );
        })}
      </div>
    </section>
  );
}
